import React, { useState, useEffect, useCallback } from 'react';
import { teamsApi } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, Users, Briefcase, Handshake } from '@phosphor-icons/react';
import { ObjectPageHeader } from '../components/fiori';

const ORIGIN = typeof window !== 'undefined' && window.location?.origin
  ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const API = `${ORIGIN}/api`;

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u, p, d] = await Promise.all([
        teamsApi.list(),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/deals?limit=100`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTeams(t.data); setUsers(u.data); setProjects(p.data); setDeals(d.data);
    } catch (e) { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await teamsApi.delete(id); toast.success('Team deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const totalMembers = teams.reduce((acc, t) => acc + (t.members?.length || 0), 0);
  const linkedWork = teams.reduce((acc, t) => acc + (t.linked_projects?.length || 0) + (t.linked_deals?.length || 0), 0);
  const kpis = [
    { label: 'Teams',         value: teams.length },
    { label: 'Team members',  value: totalMembers,  color: '#41B6E6' },
    { label: 'Linked work',   value: linkedWork,    color: '#00B140' },
  ];

  return (
    <div data-testid="teams-page">
      <ObjectPageHeader
        breadcrumb={['Home', 'Team Management']}
        title="Team Management"
        subtitle="Cross-functional project teams linked to deals and projects"
        kpis={kpis}
        actions={
          <Button className="rounded-sm h-9" style={{ background: '#002677' }} onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="team-create-btn">
            <Plus size={16} className="mr-1.5" /> New team
          </Button>
        }
      />

      <div className="p-6">
        {loading ? (
          <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div>
        ) : teams.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-sm">
            No teams yet — create your first cross-functional team.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((t) => (
              <TeamCard key={t.id} team={t} projects={projects} deals={deals}
                        onEdit={() => { setEditing(t); setDialogOpen(true); }}
                        onDelete={() => del(t.id)} />
            ))}
          </div>
        )}
      </div>

      {dialogOpen && (
        <TeamForm
          team={editing}
          users={users}
          projects={projects}
          deals={deals}
          onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function TeamCard({ team, projects, deals, onEdit, onDelete }) {
  const linkedProjectNames = (team.linked_projects || [])
    .map(pid => projects.find(p => p.id === pid)?.name).filter(Boolean);
  const linkedDealTitles = (team.linked_deals || [])
    .map(did => deals.find(d => d.id === did)?.title).filter(Boolean);
  return (
    <div className="bg-card border border-border p-4 rounded-sm hover:shadow-md transition-shadow" data-testid={`team-${team.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-semibold truncate">{team.name}</h3>
          {team.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{team.description}</p>}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onEdit} data-testid={`edit-${team.id}`}>✎</Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onDelete} data-testid={`delete-${team.id}`}><Trash size={14} /></Button>
        </div>
      </div>

      {/* Member avatars */}
      <div className="mt-3 flex items-center -space-x-2">
        {(team.member_details || []).slice(0, 6).map((m) => (
          <div key={m.id} className="h-8 w-8 rounded-full bg-[#002677] text-white text-[10px] font-semibold flex items-center justify-center border-2 border-card" title={m.name}>
            {m.picture ? <img src={m.picture} alt="" className="h-full w-full rounded-full object-cover" /> :
              (m.name || '').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
          </div>
        ))}
        {(team.member_details?.length || 0) > 6 && (
          <div className="h-8 w-8 rounded-full bg-muted text-xs flex items-center justify-center border-2 border-card">
            +{team.member_details.length - 6}
          </div>
        )}
        {(!team.member_details || team.member_details.length === 0) && (
          <span className="text-xs text-muted-foreground">No members yet</span>
        )}
      </div>

      {/* Stats strip */}
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
        <Stat icon={Users} label="Members" value={team.members?.length || 0} />
        <Stat icon={Briefcase} label="Projects" value={linkedProjectNames.length} />
        <Stat icon={Handshake} label="Deals" value={linkedDealTitles.length} />
      </div>

      {linkedProjectNames.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {linkedProjectNames.slice(0, 3).map((n, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 bg-[#41B6E6]/15 text-[#1E7CA6] rounded-sm">{n}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <Icon size={14} className="mx-auto text-muted-foreground" />
      <div className="text-lg font-semibold mt-0.5">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function TeamForm({ team, users, projects, deals, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: team?.name || '',
    description: team?.description || '',
    lead_id: team?.lead_id || '',
    members: team?.members || [],
    linked_projects: team?.linked_projects || [],
    linked_deals: team?.linked_deals || [],
  });
  const [saving, setSaving] = useState(false);
  const toggle = (key, id) => setForm(f => ({
    ...f, [key]: f[key].includes(id) ? f[key].filter(x => x !== id) : [...f[key], id],
  }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (team) await teamsApi.update(team.id, form);
      else      await teamsApi.create(form);
      toast.success(team ? 'Team updated' : 'Team created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{team ? 'Edit team' : 'New team'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <Field label="Team name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Description"><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label={`Members (${form.members.length} selected)`}>
            <MultiPicker
              items={users.map(u => ({ id: u.id, label: u.name, sub: u.email }))}
              selected={form.members}
              toggle={(id) => toggle('members', id)}
              emptyLabel="No users in workspace"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={`Linked projects (${form.linked_projects.length})`}>
              <MultiPicker
                items={projects.map(p => ({ id: p.id, label: p.name, sub: p.code }))}
                selected={form.linked_projects}
                toggle={(id) => toggle('linked_projects', id)}
                emptyLabel="No projects"
              />
            </Field>
            <Field label={`Linked deals (${form.linked_deals.length})`}>
              <MultiPicker
                items={deals.map(d => ({ id: d.id, label: d.title, sub: d.company_name }))}
                selected={form.linked_deals}
                toggle={(id) => toggle('linked_deals', id)}
                emptyLabel="No deals"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="rounded-sm" style={{ background: '#002677' }} disabled={saving}>
              {saving ? 'Saving…' : team ? 'Save changes' : 'Create team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MultiPicker({ items, selected, toggle, emptyLabel }) {
  return (
    <div className="border border-border rounded-sm max-h-44 overflow-y-auto divide-y divide-border">
      {items.length === 0 ? (
        <div className="p-3 text-xs text-muted-foreground text-center">{emptyLabel}</div>
      ) : items.map((it) => {
        const isOn = selected.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => toggle(it.id)}
            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/40 ${isOn ? 'bg-[#002677]/5' : ''}`}
          >
            <div className="min-w-0">
              <div className="truncate">{it.label}</div>
              {it.sub && <div className="text-[10px] text-muted-foreground truncate">{it.sub}</div>}
            </div>
            <div className={`h-4 w-4 border rounded-sm flex items-center justify-center text-[10px] ${isOn ? 'bg-[#002677] border-[#002677] text-white' : 'border-border'}`}>
              {isOn ? '✓' : ''}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
